import { streamClient, fetchChatToken } from "../config/stream";

// Connection state management
let connectionPromise = null; // Shared promise for connection attempts
let lastUserId = null;
let isConnecting = false; // Lock to prevent race conditions
let lastConnectCallTime = 0; // Track when we last called connectUser to prevent rapid calls
let connectUserInProgress = false; // Flag to prevent concurrent connectUser calls
const MIN_CONNECT_INTERVAL = 2000; // Minimum 2 seconds between connectUser calls

/**
 * Ensures the stream client is connected before performing operations
 * Uses a promise queue to prevent multiple simultaneous connection attempts
 * @param {Function} operation - The operation to perform with the stream client
 * @param {string} [requiredUserId] - Optional user ID that must be connected
 * @returns {Promise<any>} - The result of the operation
 */
export const ensureStreamConnection = async (operation, requiredUserId = null) => {
  // Retry loop to handle race conditions
  let retries = 0;
  const MAX_RETRIES = 10;
  
  while (retries < MAX_RETRIES) {
    try {
      // Check if already connected to the correct user
      const currentUserId = streamClient.userID;
      const isAlreadyConnected = streamClient.isConnected && 
                                 streamClient.userID && 
                                 (!requiredUserId || currentUserId === requiredUserId);
      
      // If already connected, proceed with operation immediately
      if (isAlreadyConnected) {
        return await operation();
      }

      // If a connection is in progress, wait for it
      if (connectionPromise) {
        try {
          await connectionPromise;
        } catch (error) {
          // Connection failed, will retry
        }
        
        // Re-check connection after waiting
        const currentUserIdAfterWait = streamClient.userID;
        const isConnectedAfterWait = streamClient.isConnected && 
                                     streamClient.userID && 
                                     (!requiredUserId || currentUserIdAfterWait === requiredUserId);
        
        if (isConnectedAfterWait) {
          return await operation();
        }
        // Connection completed but wrong user or not connected, need new connection
        // Continue to create new connection below
      }

      // Wait for any lock to clear (in case someone is about to create a promise)
      if (isConnecting) {
        while (isConnecting) {
          await new Promise(resolve => setTimeout(resolve, 50));
        }
        // Re-check connection after waiting
        const currentUserIdAfterWait = streamClient.userID;
        const isConnectedAfterWait = streamClient.isConnected && 
                                     streamClient.userID && 
                                     (!requiredUserId || currentUserIdAfterWait === requiredUserId);
        
        if (isConnectedAfterWait) {
          return await operation();
        }
      }

      // Final check before creating connection
      const finalCheckUserId = streamClient.userID;
      const finalIsConnected = streamClient.isConnected && 
                               streamClient.userID && 
                               (!requiredUserId || finalCheckUserId === requiredUserId);
      
      if (finalIsConnected) {
        return await operation();
      }

      // Now create connection - use atomic check-and-set to prevent race conditions
      // Check one more time right before setting lock (atomic operation)
      if (isConnecting || connectionPromise) {
        // Race condition detected - retry from beginning
        retries++;
        await new Promise(resolve => setTimeout(resolve, 50));
        continue;
      }
      
      // CRITICAL: Atomically set lock FIRST - this prevents other callers from creating new connections
      // Set lock synchronously before any awaits to make it truly atomic
      isConnecting = true;
      
      // Double-check connectionPromise wasn't set between check and lock (race condition check)
      if (connectionPromise) {
        // Someone else created it while we were setting lock - release our lock and wait for theirs
        isConnecting = false;
        await connectionPromise;
        return await operation();
      }
      
      // Create connection promise - module-level variables are intentionally shared
      // eslint-disable-next-line no-loop-func
      connectionPromise = (async () => {
        try {
          // Determine which user ID to connect with
          const userId = requiredUserId || streamClient.userID || lastUserId;

          if (!userId) {
            throw new Error("No user ID available for connection");
          }

          // Store the user ID
          lastUserId = userId;

          // Get user info from session storage
          const userInfo = JSON.parse(sessionStorage.getItem("user") || "{}");

          // CRITICAL: Final check before connecting - only connect if not already connected
          // This prevents calling connectUser if another caller already connected us
          if (streamClient.isConnected && streamClient.userID === userId) {
            // Already connected to correct user by another caller, we're done
            return;
          }
          
          // CRITICAL: Check connection status and flags BEFORE any async operations
          // This prevents race conditions where multiple callers pass checks simultaneously
          if (streamClient.isConnected && streamClient.userID === userId) {
            // Already connected, no need to connect again
            return;
          }
          
          // Check time interval synchronously (before async operations)
          const timeSinceLastCall = Date.now() - lastConnectCallTime;
          if (timeSinceLastCall < MIN_CONNECT_INTERVAL) {
            // Too soon since last call, wait synchronously if possible, otherwise return early
            // We'll wait in the async context below
          }
          
          // Check if connectUser is already in progress (synchronously)
          if (connectUserInProgress) {
            // Another connectUser call is in progress, wait for it
            while (connectUserInProgress) {
              await new Promise(resolve => setTimeout(resolve, 100));
            }
            // Check connection after waiting
            if (streamClient.isConnected && streamClient.userID === userId) {
              return; // Connected by another call
            }
          }
          
          // Now check time interval again (async check after waiting for in-progress call)
          const timeSinceLastCallAfterWait = Date.now() - lastConnectCallTime;
          if (timeSinceLastCallAfterWait < MIN_CONNECT_INTERVAL) {
            // Too soon since last call, wait
            await new Promise(resolve => setTimeout(resolve, MIN_CONNECT_INTERVAL - timeSinceLastCallAfterWait));
          }
          
          // Final synchronous check before setting flag
          if (connectUserInProgress) {
            // Flag was set while we were waiting, wait again
            while (connectUserInProgress) {
              await new Promise(resolve => setTimeout(resolve, 100));
            }
            if (streamClient.isConnected && streamClient.userID === userId) {
              return;
            }
          }
          
          // Final connection check
          if (streamClient.isConnected && streamClient.userID === userId) {
            return;
          }
          
          // NOW we can safely set the flag and call connectUser
          // Set flag synchronously BEFORE any async operations
          connectUserInProgress = true;
          lastConnectCallTime = Date.now();
          
          try {
            const token = await fetchChatToken(userId);
            
            // One more check after getting token (but flag is already set)
            if (!streamClient.isConnected || streamClient.userID !== userId) {
              await streamClient.connectUser(
                {
                  id: userId,
                  name: userInfo.name || "User",
                  image: userInfo.photoUrl || "",
                },
                token,
              );
              // Small delay to ensure connection is fully established
              await new Promise(resolve => setTimeout(resolve, 300));
            }
          } catch (tokenError) {
            console.warn(
              "Token fetch failed, using dev token fallback:",
              tokenError.message,
            );
            // Fallback connect with dev token
            if (!streamClient.isConnected || streamClient.userID !== userId) {
              await streamClient.connectUser(
                {
                  id: userId,
                  name: userInfo.name || "User",
                  image: userInfo.photoUrl || "",
                },
                streamClient.devToken(userId),
              );
              // Small delay to ensure connection is fully established
              await new Promise(resolve => setTimeout(resolve, 300));
            }
          } finally {
            // Always clear the flag, even on error
            connectUserInProgress = false;
          }
        } catch (error) {
          console.error("Connection error:", error);
          throw error;
        } finally {
          // Always clear the lock and promise, even on error
          connectionPromise = null;
          isConnecting = false;
        }
      })();

      // Wait for connection to complete
      await connectionPromise;

      // Now perform the requested operation
      return await operation();
    } catch (error) {
      // If it's a connection error and we haven't exceeded retries, retry
      if (retries < MAX_RETRIES && (error.message.includes("connection") || error.message.includes("connectUser"))) {
        retries++;
        await new Promise(resolve => setTimeout(resolve, 100));
        continue;
      }
      console.error("Error during Stream operation:", error);
      throw error;
    }
  }
  
  // If we exhausted retries, throw error
  throw new Error("Failed to establish Stream connection after maximum retries");
};

/**
 * Safely mark a channel as read by ensuring connection first
 */
export const safeMarkChannelRead = async (channel) => {
  // Only ensure connection if not already connected
  if (!streamClient.isConnected) {
  return ensureStreamConnection(() => channel.markRead());
  } else {
    return channel.markRead();
  }
};

/**
 * Store the user ID when connecting, so we can use it for reconnection
 */
export const setLastUserId = (userId) => {
  if (userId) {
    lastUserId = userId;
  }
};

/**
 * Force refresh channels for a user to ensure all membership changes are reflected
 * This is especially important after joining a premium group
 * Now uses ensureStreamConnection to handle connection properly
 */
export const refreshUserChannels = async (userId) => {
  try {
    // Use ensureStreamConnection to handle connection properly
    // Pass userId to ensure it connects to the correct user
    await ensureStreamConnection(async () => {
      // Operation succeeds if we get here - ensureStreamConnection handles connection
      return true;
    }, userId);

    // Step 2: Now query all channels with this fresh connection
    const filter = {
      members: { $in: [userId] },
      type: {
        $in: [
          "standard_group",
          "premium_group",
          "premium_individual_class",
          "one_to_one_chat",
        ],
      },
    };

    // Step 3: Force a clean query with watching enabled - increase limit to catch more channels
    const channels = await streamClient.queryChannels(
      filter,
      { last_message_at: -1 },
      {
        watch: true,
        state: true,
        presence: true,
        limit: 100, // Increased limit to catch more channels
      },
    );

    // Step 4: Update channel names from Firestore for all channels
    const { db } = await import("../firebaseConfig");
    const { doc, getDoc } = await import("firebase/firestore");

    // Import enhanced channel update utility
    const { enforceChannelNameFromFirestore } = await import(
      "../utils/channelRefreshUtil"
    );

    // Process all channels, not just premium ones
    for (const channel of channels) {
      try {
        // Use the enhanced channel name enforcement utility
        await enforceChannelNameFromFirestore(channel);

        // Additional fallback - for groups, sync with groups collection
        if (
          channel.type === "premium_group" ||
          channel.type === "standard_group"
        ) {
          const groupDoc = await getDoc(doc(db, "groups", channel.id));
          if (groupDoc.exists()) {
            const groupData = groupDoc.data();
            if (groupData.groupName) {
              channel.data.name = groupData.groupName;
              if (channel.name !== groupData.groupName) {
                channel.name = groupData.groupName; // Also set directly on channel object
              }
              channel.data.image = groupData.imageUrl || channel.data.image;

              // Force update the state
              if (channel.state) {
                channel.state.name = groupData.groupName;
              }
            }
          }
        }
        // For classes, sync with classes collection
        else if (channel.type === "premium_individual_class") {
          const classDoc = await getDoc(doc(db, "classes", channel.id));
          if (classDoc.exists()) {
            const classData = classDoc.data();
            if (classData.className) {
              channel.data.name = classData.className;
              if (channel.name !== classData.className) {
                channel.name = classData.className; // Also set directly on channel object
              }
              channel.data.image = classData.imageUrl || channel.data.image;

              // Force update the state
              if (channel.state) {
                channel.state.name = classData.className;
              }
            }
          }
        }

        // Force watch the channel to refresh local state
        await channel.watch();
      } catch (error) {
        console.error(`Error updating channel ${channel.id}: ${error.message}`);
      }
    }

    // Return detailed channel info for debugging
    return channels.map((channel) => ({
      id: channel.id,
      type: channel.type,
      name: channel.data?.name,
      members: Object.keys(channel.state?.members || {}).length,
    }));
  } catch (error) {
    console.error(`Error refreshing channels: ${error.message}`);
    return false;
  }
};
